import { ButtonType, DropdownButtonType, List, SingleSelectDropdown } from '@tableau/widgets';
import React from 'react';
import _ = require('underscore');

import { ButtonWithSpinner } from '~commonInfra/components/ButtonWithSpinner';
import { CommonErrorCode } from '~commonInfra/utils/ErrorCodes';
import { Set } from '~commonInfra/utils/Set';

import { Formatters, Messages } from '../../codegen/Localize';
import { IError } from '../../httpClient/serverApi/types/IError';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import {
  getSessionManagementServiceInstance,
  SessionManagementService
} from '../../sessionManagement/SessionManagementService';
import { ADMIN_PROFILE_NONE_VALUE, isAdminProfileChoiceEnabled } from '../helpers/AdminProfileConstants';
import {
  ADMIN_PROFILE_DROPDOWN_WIDTH,
  ADMIN_PROFILE_LIST_MAX_HEIGHT,
  getAdminProfileListItems
} from '../helpers/AdminProfileDropdownHelper';
import { AdminProfileUpdateActionNotification } from '../services/notifications/AdminProfileUpdateActionNotification';
import { ServerAdminUpdateActionNotification } from '../services/notifications/ServerAdminUpdateActionNotification';
import { SiteRolesUpdateActionNotification } from '../services/notifications/SiteRolesUpdateActionNotification';
import { AdminProfilesResource } from '../services/resources/AdminProfilesResource';
import { SiteRoleOption, SiteRoleOptionsResource } from '../services/resources/SiteRoleOptions';
import { SiteRolesResource } from '../services/resources/SiteRoles';
import { SiteUsers } from '../services/resources/SiteUsers';
import { ISiteUserInfo } from '../users/models/ISiteUserInfo';
import { IUserInfo } from '../users/models/IUserInfo';
import { ISiteAdminProfile } from '../users/serverApi/requests/GetSiteAdminProfilesRequest';
import { ISetUsersAdminProfileResult } from '../users/serverApi/requests/SetUsersAdminProfileRequest';
import { IUpdateUsersAdminStatusResult } from '../users/serverApi/requests/UpdateUsersAdminStatusRequest';
import { SiteRoles } from '../users/serverApi/types/ISiteRole';
import { SiteRolePickerMenu } from './siteRolePicker/SiteRolePickerMenu';

export interface SetSiteUsersRoleDialogState {
  readonly siteRole: string;
  readonly busy: boolean;
  readonly adminProfiles: ISiteAdminProfile[];
  readonly adminProfile: string;
}

export interface SetSiteUsersRoleDialogProps {
  readonly close: () => void;
  readonly siteUsersToSet: Set<ISiteUserInfo>;
  readonly dismiss: () => void;
}

const SiteUserSiteRoleDropdownWidth = 370;

const SetSiteUsersRoleDialogStyle = {
  width: '400px'
};
export class SetSiteUsersRoleDialog extends React.Component<SetSiteUsersRoleDialogProps, SetSiteUsersRoleDialogState> {

  private adminProfileUpdateActionNotification: AdminProfileUpdateActionNotification = AdminProfileUpdateActionNotification.instance;
  private serverAdminUpdateActionNotification: ServerAdminUpdateActionNotification = ServerAdminUpdateActionNotification.instance;
  private server: SessionManagementService = getSessionManagementServiceInstance();
  private siteRoles: SiteRolesResource = SiteRolesResource.instance;
  private siteRolesOptionsResource: SiteRoleOptionsResource = SiteRoleOptionsResource.instance;
  private siteRolesUpdateActionNotification: SiteRolesUpdateActionNotification = SiteRolesUpdateActionNotification.instance;

  private initialAdminProfile: string;
  private siteRoleOptions = this.siteRoles.optionsForCurrentUser();

  private readonly onSiteRoleSelected = (roleOption: SiteRoleOption) => {
    const newRole = roleOption.role;
    const adminProfile = isAdminProfileChoiceEnabled(newRole)
      ? this.initialAdminProfile
      : ADMIN_PROFILE_NONE_VALUE;
    this.setState({ siteRole: newRole, adminProfile });
  };

  public readonly setSiteRoleCallback = () => {
    const adminProfileLuid = isAdminProfileChoiceEnabled(this.state.siteRole)
      && this.state.adminProfile !== ADMIN_PROFILE_NONE_VALUE
      ? this.state.adminProfile
      : undefined;
    this.setSiteUsersSiteRoles(this.props.siteUsersToSet, this.state.siteRole, adminProfileLuid);
  };
  public setSiteUsersSiteRoles(selectedUsers: Set<IUserInfo>, siteRole: string, adminProfileLuid?: string): Promise<any> {
    this.setState({ busy: true });
    const users = selectedUsers.clone();
    let setSiteUsersSiteRolesPromise: Promise<any>;
    if (siteRole === SiteRoles.ServerAdministrator) {
      setSiteUsersSiteRolesPromise = this.makeUsersServerAdminThenDisplayToast(users);
    } else {
      setSiteUsersSiteRolesPromise = this.demoteServerAdminsUpdateSiteRolesThenDisplayToasts(users, siteRole, adminProfileLuid);
    }
    setSiteUsersSiteRolesPromise.finally(this.props.close);
    return setSiteUsersSiteRolesPromise;
  }

  public makeUsersServerAdminThenDisplayToast(users: Set<IUserInfo>): Promise<any> {
    return SiteUsers.instance.updateServerAdminStatusAndDelay(users.keys(), true)
      .then((result: IUpdateUsersAdminStatusResult) => {
        this.serverAdminUpdateActionNotification.notify(result, users, true);
      });
  }

  public demoteServerAdminsUpdateSiteRolesThenDisplayToasts(users: Set<IUserInfo>, siteRole: string, adminProfileLuid?: string): Promise<any> {
    const serverAdmins = users.filter((user: ISiteUserInfo) => user.siteRole === SiteRoles.ServerAdministrator);
    let showToastForDemotedAdmins = () => { };
    let usersWithoutErrors: Set<IUserInfo>;
    return this.demoteServerAdminsNoDelay(serverAdmins)
      .then((demoteServerAdminResult: IResult) => {
        usersWithoutErrors = this.getUsersWithoutErrorsInResults(demoteServerAdminResult, users);
        showToastForDemotedAdmins = (() => this.serverAdminUpdateActionNotification.notify(demoteServerAdminResult, serverAdmins, false));
        return SiteUsers.instance.updateUsersSiteRole(usersWithoutErrors.keys(), siteRole);
      })
      .then((updateUsersSiteRoleResult: IResult) => {
        showToastForDemotedAdmins();
        if (usersWithoutErrors.size() > 0) {
          this.siteRolesUpdateActionNotification.notify(updateUsersSiteRoleResult, usersWithoutErrors, siteRole);
          const userLuids = usersWithoutErrors.toArray().map((u: IUserInfo) => (u as ISiteUserInfo).luid).filter(Boolean) as string[];
          const adminProfileLuidToSet = isAdminProfileChoiceEnabled(siteRole) ? adminProfileLuid : undefined;
          const effectiveInitial = this.initialAdminProfile === ADMIN_PROFILE_NONE_VALUE ? undefined : this.initialAdminProfile;
          const adminProfileChanged = adminProfileLuidToSet !== effectiveInitial;
          if (userLuids.length > 0 && adminProfileChanged && this.server.isAdminProfilesEnabled() && this.server.isSessionSiteOrServerAdmin()) {
            return SiteUsers.instance.setUsersAdminProfile(userLuids, adminProfileLuidToSet)
              .then((adminProfileResult: ISetUsersAdminProfileResult) => {
                const resultAsIResult: IResult = { errors: adminProfileResult.errors || [] };
                const usersByLuid = new Set<IUserInfo>((u: IUserInfo) => (u as ISiteUserInfo).luid);
                usersWithoutErrors.toArray().forEach((u: IUserInfo) => usersByLuid.add(u));
                this.adminProfileUpdateActionNotification.notify(resultAsIResult, usersByLuid);
                return updateUsersSiteRoleResult;
              })
              .catch(() => Promise.resolve(updateUsersSiteRoleResult));
          }
        }
        return Promise.resolve(updateUsersSiteRoleResult);
      });
  }

  public demoteServerAdminsNoDelay(serverAdmins: Set<IUserInfo>): Promise<IResult> {
    if (serverAdmins.size() === 0) {
      return Promise.resolve({});
    } else if (!this.server.isSessionServerAdmin()) {
      return this.generateErrorsForUsersWithInsufficentPermissions(serverAdmins);
    } else {
      return SiteUsers.instance.updateServerAdminStatusNoDelay(serverAdmins.keys(), false);
    }
  }

  public generateErrorsForUsersWithInsufficentPermissions(serverAdmins: Set<IUserInfo>): Promise<IResult> {
    const result: IResult = { errors: [] };
    this.addErrorResultsForCallingUsersWithInsufficientPermissions(serverAdmins, result);
    return Promise.resolve(result);
  }

  public addErrorResultsForCallingUsersWithInsufficientPermissions(serverAdmins: Set<IUserInfo>, result: IResult) {
    serverAdmins.each((user: ISiteUserInfo) => {
      result.errors.push({
        id: user.id,
        code: CommonErrorCode.CallingUserInsufficientPermissions
      });
    });
  }

  public getUsersWithoutErrorsInResults(result: IResult, users: Set<IUserInfo>): Set<IUserInfo> {
    let usersWithoutErrors = users.clone();
    if (result && result.errors) {
      _.each(result.errors, (error: IError) => {
        usersWithoutErrors.remove(usersWithoutErrors.get(error.id));
      });
    }
    return usersWithoutErrors;
  }

  public constructor(props: SetSiteUsersRoleDialogProps) {
    super(props);
    const firstUser = props.siteUsersToSet.size() === 1 ? props.siteUsersToSet.first() as ISiteUserInfo : null;
    this.initialAdminProfile = firstUser?.adminProfile?.luid ?? ADMIN_PROFILE_NONE_VALUE;
    const siteRole = firstUser?.siteRole ?? SiteRoles.Unlicensed;
    this.state = {
      siteRole,
      busy: false,
      adminProfiles: [],
      adminProfile: isAdminProfileChoiceEnabled(siteRole) ? this.initialAdminProfile : ADMIN_PROFILE_NONE_VALUE
    };
  }

  public componentDidMount(): void {
    if (!this.server.isAdminProfilesEnabled() || !this.server.isSessionSiteOrServerAdmin()) {
      return;
    }
    AdminProfilesResource.instance.getProfiles()
      .then((profiles: ISiteAdminProfile[]) => {
        let list = profiles;
        const firstUser = this.props.siteUsersToSet.size() === 1 ? this.props.siteUsersToSet.first() as ISiteUserInfo : null;
        if (firstUser?.adminProfile && this.state.adminProfile !== ADMIN_PROFILE_NONE_VALUE
            && !profiles.some((p: ISiteAdminProfile) => p.luid === firstUser.adminProfile!.luid)) {
          const userProfile: ISiteAdminProfile = {
            luid: firstUser.adminProfile!.luid,
            name: firstUser.adminProfile!.name,
            description: firstUser.adminProfile!.description
          };
          list = [userProfile].concat(profiles);
        }
        this.setState({ adminProfiles: list });
      })
      .catch(() => { });
  }

  public render() {
    return (
      <div className="tb-set-site-users-role-modal" style={SetSiteUsersRoleDialogStyle}>
        <div className="tb-dialog-title">
          {Messages.confirmSetUsersSiteRoleTitle()}
        </div>
        <div className="tb-dialog-body-input-description">
          {this.getDialogDescription(this.props.siteUsersToSet.first().displayName, this.props.siteUsersToSet.size())}
        </div>
        <div className="tb-select-role-dropdown-container tb-row-with-icon-on-right tb-margin-top-wide">
          <SiteRolePickerMenu
            siteRoleOptions={this.siteRoleOptions.map((siteRole: string) => this.siteRolesOptionsResource.getSiteRoleOptionBySiteRole(siteRole))}
            getSiteRoleOptionBySiteRole={(siteRole: string) => this.siteRolesOptionsResource.getSiteRoleOptionBySiteRole(siteRole)}
            onOptionSelected={this.onSiteRoleSelected}
            selectedRole={this.siteRolesOptionsResource.getSiteRoleOptionBySiteRole(this.state.siteRole)}
            isDisabled={this.state.busy}
            showSiteRoleInfoLink={true}
          />
        </div>
        {this.server.isAdminProfilesEnabled() && this.server.isSessionSiteOrServerAdmin() && (
          <div className="tb-select-role-dropdown-container tb-row-with-icon-on-right tb-margin-top-wide">
            <span className="tb-react-text-input-label-cell" style={{ marginRight: '8px' }}>{Messages.adminProfileLabel()}</span>
            <SingleSelectDropdown
              label=""
              value={isAdminProfileChoiceEnabled(this.state.siteRole) ? this.state.adminProfile : ADMIN_PROFILE_NONE_VALUE}
              onChange={(value: string) => this.setState({ adminProfile: value })}
              list={<List items={getAdminProfileListItems(this.state.adminProfiles || [])} style={{ maxHeight: ADMIN_PROFILE_LIST_MAX_HEIGHT, overflow: 'auto' }} />}
              type={DropdownButtonType.Outline}
              disabled={this.state.busy || !isAdminProfileChoiceEnabled(this.state.siteRole)}
              testId="set-site-users-role-admin-profile-picker"
              aria-label={Messages.adminProfileLabel()}
              style={{ width: ADMIN_PROFILE_DROPDOWN_WIDTH }}
            />
          </div>
        )}
        <div className="tb-dialog-actions">
          <ButtonWithSpinner
            buttonType={ButtonType.Outline}
            onClick={this.props.dismiss}
            testId={'confirm-action-dialog-cancel'}
            children={Messages.cancel()}
            disabled={this.state.busy}
          />
          <span className="tb-margin-left-wide">
            <ButtonWithSpinner
              buttonType={ButtonType.Primary}
              showSpinner={this.state.busy}
              onClick={this.setSiteRoleCallback}
              testId={'confirm-action-dialog-submit'}
              children={Messages.confirmSetUsersSiteRoleButton({
                ITEM_COUNT: this.props.siteUsersToSet.size(),
                ITEM_COUNT_LOC: Formatters.formatCount(this.props.siteUsersToSet.size())
              })}
            />
          </span>
        </div>
      </div>
    );
  }

  private getDialogDescription(displayName: string, userCount: number) {
    return Messages.confirmSetUsersSiteRoleDesc(
      {
        USER_NAME: displayName,
        USER_COUNT: userCount,
        USER_COUNT_LOC: Formatters.formatCount(userCount)
      });
  }
}
